import { Alert, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AutocompleteElement, CheckboxElement, SelectElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker, useNavigate } from "react-router";

import { AutocompleteModel, GqlMapEditorLayerStyle } from "~/__generated__";
import useAutocompleteGetOptions from "~/modules/app/api/useAutocompleteGetOptions";
import GaiaColorPicker from "~/modules/app/components/GaiaColorPicker";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { GqlProjectMetadata } from "~/modules/project/api/useMeGetProjectInfoQuery";

import useMapEditorLayerDelete from "../../api/useMapEditorLayerDelete";
import useMapLayerUpdate from "../../api/useMapEditorLayerUpdate";
import { MapEditorLayer } from "../../types/mapEditorTypes";
import { MapEditorLayerEditFormData } from "./types";

type Props = {
    project: GqlProjectMetadata;
    data: MapEditorLayer;
    layerId: string;
};

function createFormValuesFromLayer(data: MapEditorLayer): MapEditorLayerEditFormData {
    return {
        name: data.name,
        color: data.color,
        style: data.style,
        config: {
            showDuringFieldwork: data.config.showDuringFieldwork ?? [],
        },
        showToClientUser: data.showToClientUser,
    };
}

export default function MapEditorLayerForm({ project, data, layerId }: Props) {
    const { t } = useTranslation("mapEditor");
    const { layerUpdate, error } = useMapLayerUpdate();
    const { layerDelete, loading: deleteLoading } = useMapEditorLayerDelete();
    const [autoCompleteValue, setAutoCompleteValue] = useState("");

    const { loading: autocompleteLoading, data: autocompleteData } = useAutocompleteGetOptions({
        model: AutocompleteModel.Protocol,
        q: autoCompleteValue,
        params: {
            projectId: project.id,
        },
    });

    const navigate = useNavigate();

    const formContext = useForm<MapEditorLayerEditFormData>({
        defaultValues: createFormValuesFromLayer(data),
    });

    const {
        setError,
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
    } = formContext;

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    const onSubmit = async (formData: MapEditorLayerEditFormData) => {
        await layerUpdate({ projectId: project.id, layerId, ...formData });
        reset(formData);
    };

    const handleDelete = async () => {
        await layerDelete({ projectId: project.id, layerId });
        navigate("../", { replace: true });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}
                <TextFieldElement
                    name="name"
                    size="medium"
                    rules={{ required: true }}
                    control={control}
                    label={t("mapEditorLayerDetails.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                    <SelectElement
                        control={control}
                        label={t("mapEditorLayerDetails.labelStyle")}
                        name="style"
                        fullWidth
                        options={Object.entries(GqlMapEditorLayerStyle).map(([, value]) => ({
                            id: value,
                            label: value,
                        }))}
                    />

                    <Controller
                        control={control}
                        name="color"
                        render={({ field: { onChange, value } }) => (
                            <GaiaColorPicker value={value ? value : "#ff0000"} onChange={onChange} />
                        )}
                    />
                </Stack>

                <AutocompleteElement
                    control={control}
                    label={t("mapEditorLayerDetails.labelShowDuringFieldwork")}
                    autocompleteProps={{
                        inputValue: autoCompleteValue,
                        onInputChange: (event, newInputValue) => {
                            if (!event) return;
                            setAutoCompleteValue(newInputValue);
                        },
                    }}
                    multiple
                    loading={autocompleteLoading}
                    name="config.showDuringFieldwork"
                    options={
                        autocompleteData?.map(i => ({
                            id: i.value,
                            label: i.label,
                        })) || []
                    }
                />

                <CheckboxElement
                    name="showToClientUser"
                    label={t("mapEditorLayerDetails.showToClientUser")}
                    control={control}
                />

                <Stack direction="row" spacing={2} width="100%">
                    <GaiaDeleteButton
                        label={t("mapEditorLayerDetails.btnDelete")}
                        isLoading={deleteLoading}
                        onDelete={handleDelete}
                        sx={{ flex: 1 }}
                    />
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!isDirty}
                        loading={isLoading}
                        sx={{ flex: 1 }}
                    >
                        {t("mapEditorLayerDetails.btnSave")}
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
}
